import { getUserFacingError } from "../../utils/userFacingError";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faClock,
  faRotateRight,
  faShieldHalved,
  faTriangleExclamation,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import {
  activateVerifiedOwner,
  clearAccessRequestSubmissionId,
  cleanVerificationLocation,
  clearVerificationToken,
  getVerificationSession,
  isFinalApprovedVerification,
  ownerInvitePath,
  providerNavigationUrl,
  resolveVerificationToken,
  startVerificationSession,
  verificationPollDelay,
  VERIFICATION_FAST_POLL_WINDOW_MS,
  VERIFICATION_POLL_MAX_INTERVAL_MS,
  VERIFICATION_PREPARATION_TIMEOUT_MS,
  type VerificationSessionView,
} from "../../services/accessRequestOnboarding";
import { RepresentativeVerificationHeader } from "../../components/RepresentativeVerificationHeader";
import "./representative-verification.css";

type FatalState = "invalid" | "expired" | null;

export const PROVIDER_RETURN_RESUME_DELAY_MS = 6000;

function isRecoverableStartTransition(error: any): boolean {
  const status = Number(error?.response?.status || 0);
  const code = String(error?.response?.data?.code || "");
  return (
    code === "provider_reconciliation_pending" ||
    code === "session_starting" ||
    !error?.response ||
    status === 408 ||
    status === 409 ||
    status === 425 ||
    status >= 500
  );
}

function canApplyLateStartResponse(
  current: VerificationSessionView | null,
  next: VerificationSessionView,
): boolean {
  if (!current) return true;
  if (current.outcomeClass !== "pending" || next.outcomeClass !== "pending") return false;
  const rank: Record<VerificationSessionView["verificationStatus"], number> = {
    invited: 0,
    starting: 1,
    provider_error: 1,
    in_progress: 2,
    manual_review: 3,
    approved: 4,
    declined: 4,
    expired: 4,
  };
  return rank[next.verificationStatus] >= rank[current.verificationStatus];
}

function navigateToProvider(url: string): void {
  window.location.assign(url);
}

function StatePanel({
  icon,
  title,
  body,
  tone = "neutral",
  children,
}: {
  icon: IconDefinition;
  title: string;
  body: string;
  tone?: "neutral" | "success" | "warning" | "danger";
  children?: React.ReactNode;
}) {
  return (
    <section className={`verification-state is-${tone}`} aria-live="polite">
      <img
        className="verification-state-logo"
        src={`${window.location.origin}/assets/Logo_DAKASA_V1_Preto.png`}
        alt="DaKasa LTDA"
      />
      <span className="verification-state-icon" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>
      <h1>{title}</h1>
      <p>{body}</p>
      {children && <div className="verification-state-actions">{children}</div>}
    </section>
  );
}

export const RepresentativeVerificationPage: React.FC<{
  providerNavigate?: (url: string) => void;
}> = ({ providerNavigate = navigateToProvider }) => {
  const [returnedFromProvider] = React.useState(() =>
    new URLSearchParams(window.location.search).has("verificationSessionId"),
  );
  const [token] = React.useState(() =>
    resolveVerificationToken(window.location.hash, window.sessionStorage),
  );
  const [session, setSession] = React.useState<VerificationSessionView | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [fatalState, setFatalState] = React.useState<FatalState>(token ? null : "invalid");
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [startError, setStartError] = React.useState<string | null>(null);
  const [providerUrlError, setProviderUrlError] = React.useState<string | null>(null);
  const [pollWarning, setPollWarning] = React.useState<string | null>(null);
  const [automaticPollingPaused, setAutomaticPollingPaused] = React.useState(false);
  const [consent, setConsent] = React.useState(false);
  const [consentError, setConsentError] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [preparationSlow, setPreparationSlow] = React.useState(false);
  const [preparationTimedOut, setPreparationTimedOut] = React.useState(false);
  const [activating, setActivating] = React.useState(false);
  const [activationRecoveryActive, setActivationRecoveryActive] = React.useState(false);
  const [accessAlreadyReleased, setAccessAlreadyReleased] = React.useState(false);
  const [activationError, setActivationError] = React.useState<string | null>(null);
  const [providerReturnResumeAvailable, setProviderReturnResumeAvailable] = React.useState(false);
  const pollEnabled = React.useRef(true);
  const mounted = React.useRef(true);
  const activationStarted = React.useRef(false);
  const activationRecoveryPending = React.useRef(false);
  const activationRecoveryCanRetryApproved = React.useRef(false);
  const awaitingProviderRedirect = React.useRef(false);
  const ambiguousStartOutcome = React.useRef(false);
  const startInFlight = React.useRef(false);
  const preparationTimer = React.useRef<number | null>(null);
  const preparationDeadlineTimer = React.useRef<number | null>(null);
  const preparationTimedOutRef = React.useRef(false);
  const requestStatusRefresh = React.useRef<() => void>(() => undefined);
  const resumeStatusPolling = React.useRef<() => void>(() => undefined);
  const startController = React.useRef<AbortController | null>(null);
  const activationController = React.useRef<AbortController | null>(null);
  const sessionObservationGeneration = React.useRef(0);
  const latestSession = React.useRef<VerificationSessionView | null>(null);
  const fastPollingUntil = React.useRef(Date.now() + VERIFICATION_FAST_POLL_WINDOW_MS);

  const openProvider = React.useCallback(
    (rawUrl: string): boolean => {
      try {
        providerNavigate(providerNavigationUrl(rawUrl, window.location.origin));
        setProviderUrlError(null);
        return true;
      } catch {
        setProviderUrlError("O endereço do fornecedor de verificação é inválido. Tente novamente.");
        return false;
      }
    },
    [providerNavigate],
  );

  const beginPreparation = React.useCallback(() => {
    fastPollingUntil.current = Date.now() + VERIFICATION_FAST_POLL_WINDOW_MS;
    preparationTimedOutRef.current = false;
    setStarting(true);
    setPreparationSlow(false);
    setPreparationTimedOut(false);
    if (preparationTimer.current !== null) {
      window.clearTimeout(preparationTimer.current);
    }
    if (preparationDeadlineTimer.current !== null) {
      window.clearTimeout(preparationDeadlineTimer.current);
    }
    preparationTimer.current = window.setTimeout(() => {
      preparationTimer.current = null;
      setPreparationSlow(true);
    }, 15000);
    preparationDeadlineTimer.current = window.setTimeout(() => {
      preparationDeadlineTimer.current = null;
      if (preparationTimer.current !== null) {
        window.clearTimeout(preparationTimer.current);
        preparationTimer.current = null;
      }
      awaitingProviderRedirect.current = false;
      ambiguousStartOutcome.current = false;
      preparationTimedOutRef.current = true;
      setStarting(false);
      setPreparationSlow(false);
      setPreparationTimedOut(true);
      setStartError(
        "Ainda não conseguimos confirmar a preparação. Ela continua no servidor e esta página seguirá consultando a mesma sessão.",
      );
    }, VERIFICATION_PREPARATION_TIMEOUT_MS);
  }, []);

  const finishPreparation = React.useCallback(() => {
    preparationTimedOutRef.current = false;
    if (preparationTimer.current !== null) {
      window.clearTimeout(preparationTimer.current);
      preparationTimer.current = null;
    }
    if (preparationDeadlineTimer.current !== null) {
      window.clearTimeout(preparationDeadlineTimer.current);
      preparationDeadlineTimer.current = null;
    }
    setStarting(false);
    setPreparationSlow(false);
    setPreparationTimedOut(false);
  }, []);

  React.useEffect(() => {
    mounted.current = true;
    cleanVerificationLocation(window.location, window.history);
    return () => {
      mounted.current = false;
      startController.current?.abort();
      activationController.current?.abort();
      if (preparationTimer.current !== null) {
        window.clearTimeout(preparationTimer.current);
      }
      if (preparationDeadlineTimer.current !== null) {
        window.clearTimeout(preparationDeadlineTimer.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let loadedOnce = false;
    let consecutiveFailures = 0;
    let terminalObservationActive = false;
    let settlementDeadlineAt: number | null = null;
    let automaticPollingActive = true;
    let pollTimer: number | null = null;
    let requestController: AbortController | null = null;

    function scheduleRefresh(delay: number) {
      if (cancelled || !pollEnabled.current || !automaticPollingActive) return;
      if (settlementDeadlineAt !== null) {
        const remaining = settlementDeadlineAt - Date.now();
        if (remaining <= 0) {
          automaticPollingActive = false;
          setAutomaticPollingPaused(true);
          return;
        }
        delay = Math.min(delay, remaining);
      }
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      pollTimer = window.setTimeout(() => void refresh(), delay);
    }

    function refreshNow() {
      if (cancelled || !pollEnabled.current) return;
      consecutiveFailures = 0;
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      pollTimer = window.setTimeout(() => void refresh(), 0);
    }

    function resumePollingNow() {
      if (cancelled || !pollEnabled.current) return;
      automaticPollingActive = true;
      setAutomaticPollingPaused(false);
      refreshNow();
    }

    requestStatusRefresh.current = refreshNow;
    resumeStatusPolling.current = resumePollingNow;

    async function refresh() {
      if (inFlight || !pollEnabled.current) return;
      inFlight = true;
      requestController = new AbortController();
      try {
        const next = await getVerificationSession(token, requestController.signal);
        if (cancelled) return;
        consecutiveFailures = 0;
        sessionObservationGeneration.current += 1;
        if (activationRecoveryPending.current) {
          setActivationError(null);
          if (!isFinalApprovedVerification(next)) {
            activationStarted.current = false;
            activationRecoveryCanRetryApproved.current = true;
            if (
              next.outcomeSettled &&
              (next.outcomeClass === "hard_declined" ||
                next.outcomeClass === "retryable_not_approved")
            ) {
              activationRecoveryPending.current = false;
              activationRecoveryCanRetryApproved.current = false;
              setActivationRecoveryActive(false);
            }
          } else if (activationRecoveryCanRetryApproved.current) {
            activationStarted.current = false;
            activationRecoveryPending.current = false;
            activationRecoveryCanRetryApproved.current = false;
            setActivationRecoveryActive(false);
          }
        }
        latestSession.current = next;
        setSession(next);
        setLoadError(null);
        setPollWarning(null);
        if (next.verificationStatus !== "invited" || next.providerUrl) {
          setStartError(null);
        }
        if (next.providerUrl) {
          try {
            providerNavigationUrl(next.providerUrl, window.location.origin);
            setProviderUrlError(null);
          } catch {
            // Keep a previously surfaced URL error visible until the backend
            // replaces the untrusted destination with a valid one.
          }
        }
        setFatalState(next.verificationStatus === "expired" ? "expired" : null);
        const terminalNonApproval =
          next.outcomeClass === "hard_declined" || next.outcomeClass === "retryable_not_approved";
        terminalObservationActive = terminalNonApproval && !next.outcomeSettled;
        settlementDeadlineAt = next.outcomeSettled ? null : Date.parse(next.settlementDeadlineAt!);
        if (terminalNonApproval && next.outcomeSettled) {
          automaticPollingActive = false;
          pollEnabled.current = false;
          setAutomaticPollingPaused(false);
          clearVerificationToken(window.sessionStorage);
        } else if (next.verificationStatus === "invited" && !awaitingProviderRedirect.current) {
          // Merely opening the consent page must not poll until the token
          // expires. Attention changes can still perform one manual refresh.
          automaticPollingActive = false;
          setAutomaticPollingPaused(false);
        } else if (
          !next.outcomeSettled &&
          settlementDeadlineAt !== null &&
          settlementDeadlineAt <= Date.now()
        ) {
          automaticPollingActive = false;
          setAutomaticPollingPaused(true);
        } else {
          automaticPollingActive = true;
          setAutomaticPollingPaused(false);
        }
        if (awaitingProviderRedirect.current && next.providerUrl) {
          awaitingProviderRedirect.current = false;
          ambiguousStartOutcome.current = false;
          finishPreparation();
          openProvider(next.providerUrl);
          return;
        }
        if (
          awaitingProviderRedirect.current &&
          ambiguousStartOutcome.current &&
          next.verificationStatus === "invited"
        ) {
          awaitingProviderRedirect.current = false;
          ambiguousStartOutcome.current = false;
          finishPreparation();
          setStartError(
            "Não conseguimos confirmar o início desta vez. Você pode tentar novamente com segurança; o servidor não criará uma segunda sessão.",
          );
        }
        if (
          awaitingProviderRedirect.current &&
          (next.verificationStatus === "approved" ||
            next.verificationStatus === "manual_review" ||
            next.verificationStatus === "declined" ||
            next.verificationStatus === "expired")
        ) {
          awaitingProviderRedirect.current = false;
          ambiguousStartOutcome.current = false;
          finishPreparation();
        }
        if (
          next.outcomeClass === "hard_declined" ||
          next.outcomeClass === "retryable_not_approved"
        ) {
          awaitingProviderRedirect.current = false;
          ambiguousStartOutcome.current = false;
          finishPreparation();
          // A signed provider decision can be corrected during the backend's
          // bounded authoritative-reconciliation window. Keep the capability
          // and poll slowly so the page self-heals without asking the applicant
          // to reload or repeat a paid verification.
        }
        if (next.verificationStatus === "expired") {
          awaitingProviderRedirect.current = false;
          ambiguousStartOutcome.current = false;
          finishPreparation();
          pollEnabled.current = false;
          clearVerificationToken(window.sessionStorage);
        }
        if (
          next.verificationStatus === "approved" &&
          next.activationClaimed &&
          Boolean(next.activation)
        ) {
          pollEnabled.current = false;
          clearVerificationToken(window.sessionStorage);
        }
      } catch (error: any) {
        if (cancelled) return;
        if (awaitingProviderRedirect.current) automaticPollingActive = true;
        const status = error?.response?.status;
        if (status === 410) {
          setFatalState("expired");
          pollEnabled.current = false;
          clearVerificationToken(window.sessionStorage);
        } else if (status === 403 || status === 404) {
          setFatalState("invalid");
          pollEnabled.current = false;
          clearVerificationToken(window.sessionStorage);
        } else if (loadedOnce) {
          consecutiveFailures += 1;
          setPollWarning("A atualização automática falhou. Continuaremos tentando.");
        } else {
          consecutiveFailures += 1;
          setLoadError(
            getUserFacingError(error, {
              context: "general",
              fallback: "Não foi possível consultar a verificação agora.",
            }),
          );
        }
      } finally {
        loadedOnce = true;
        inFlight = false;
        requestController = null;
        if (!cancelled) setLoading(false);
        if (!cancelled && pollEnabled.current) {
          const nextDelay = verificationPollDelay(consecutiveFailures, terminalObservationActive);
          const slowBackgroundObservation =
            preparationTimedOutRef.current || Date.now() >= fastPollingUntil.current;
          scheduleRefresh(
            slowBackgroundObservation
              ? Math.max(nextDelay, VERIFICATION_POLL_MAX_INTERVAL_MS)
              : nextDelay,
          );
        }
      }
    }

    void refresh();
    const refreshOnAttention = () => refreshNow();
    window.addEventListener("focus", refreshOnAttention);
    window.addEventListener("online", refreshOnAttention);
    return () => {
      cancelled = true;
      requestStatusRefresh.current = () => undefined;
      resumeStatusPolling.current = () => undefined;
      requestController?.abort();
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      window.removeEventListener("focus", refreshOnAttention);
      window.removeEventListener("online", refreshOnAttention);
    };
  }, [finishPreparation, openProvider, token]);

  React.useEffect(() => {
    if (
      (session?.verificationStatus === "starting" ||
        session?.verificationStatus === "provider_error") &&
      !session.providerUrl &&
      !awaitingProviderRedirect.current &&
      !preparationTimedOut
    ) {
      awaitingProviderRedirect.current = true;
      beginPreparation();
    }
  }, [beginPreparation, preparationTimedOut, session]);

  React.useEffect(() => {
    const canResumeSameProviderSession =
      returnedFromProvider &&
      session?.verificationStatus === "in_progress" &&
      session.outcomeClass === "pending" &&
      Boolean(session.providerUrl);
    if (!canResumeSameProviderSession) {
      setProviderReturnResumeAvailable(false);
      return;
    }

    const timer = window.setTimeout(
      () => setProviderReturnResumeAvailable(true),
      PROVIDER_RETURN_RESUME_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [
    returnedFromProvider,
    session?.outcomeClass,
    session?.providerUrl,
    session?.verificationStatus,
  ]);

  const continueToPassword = React.useCallback(async () => {
    if (!token || activating) return;
    activationStarted.current = true;
    setActivating(true);
    setActivationError(null);
    const controller = new AbortController();
    activationController.current = controller;
    try {
      const activation = await activateVerifiedOwner(token, controller.signal);
      if (!mounted.current || controller.signal.aborted) return;
      pollEnabled.current = false;
      clearVerificationToken(window.sessionStorage);
      window.location.replace(ownerInvitePath(activation));
    } catch (error: any) {
      if (!mounted.current || controller.signal.aborted) return;
      const code = error?.response?.data?.code;
      if (code === "activation_already_claimed" || code === "owner_already_registered") {
        pollEnabled.current = false;
        clearVerificationToken(window.sessionStorage);
        setActivationError(null);
        setAccessAlreadyReleased(true);
      } else if (
        error?.response?.status === 409 &&
        (code === "verification_outcome_settling" || code === "owner_invitation_cancelled")
      ) {
        // Activation and provider reconciliation can cross in flight. Re-read
        // the signed aggregate instead of trapping the applicant on a stale
        // approval screen (for example after owner_invitation_cancelled).
        pollEnabled.current = true;
        activationRecoveryPending.current = true;
        activationRecoveryCanRetryApproved.current = code === "verification_outcome_settling";
        setActivationRecoveryActive(true);
        resumeStatusPolling.current();
        setActivationError(
          "Estamos confirmando o estado mais recente da verificação antes de liberar sua senha.",
        );
      } else {
        setActivationError(
          "A verificação foi aprovada, mas não conseguimos abrir a criação de senha agora. Se necessário, enviaremos um link de recuperação ao seu e-mail.",
        );
      }
    } finally {
      if (activationController.current === controller) {
        activationController.current = null;
      }
      if (mounted.current) setActivating(false);
    }
  }, [activating, token]);

  React.useEffect(() => {
    if (
      session &&
      isFinalApprovedVerification(session) &&
      session.activation &&
      !session.activationClaimed &&
      !activationStarted.current
    ) {
      void continueToPassword();
    }
  }, [continueToPassword, session]);

  async function startVerification(requireFreshConsent = true) {
    if (!token || starting || !session || startInFlight.current) return;
    const resumingStartedSession =
      session.verificationStatus === "starting" || session.verificationStatus === "provider_error";
    if (!requireFreshConsent && !resumingStartedSession) return;
    if (requireFreshConsent && !consent) {
      setConsentError("Confirme o consentimento antes de iniciar.");
      return;
    }
    startInFlight.current = true;
    beginPreparation();
    awaitingProviderRedirect.current = true;
    ambiguousStartOutcome.current = false;
    pollEnabled.current = true;
    resumeStatusPolling.current();
    setConsentError(null);
    setStartError(null);
    let keepPreparing = false;
    const observationGeneration = sessionObservationGeneration.current;
    const controller = new AbortController();
    startController.current = controller;
    try {
      const next = await startVerificationSession(
        token,
        session.consentPolicy.version,
        controller.signal,
      );
      if (!mounted.current || controller.signal.aborted) return;
      if (
        sessionObservationGeneration.current !== observationGeneration &&
        !canApplyLateStartResponse(latestSession.current, next)
      ) {
        const current = latestSession.current;
        keepPreparing =
          Boolean(current && !current.providerUrl) &&
          (current?.verificationStatus === "starting" ||
            current?.verificationStatus === "provider_error");
        return;
      }
      latestSession.current = next;
      setSession(next);
      if (next.providerUrl) {
        const shouldOpenProvider = awaitingProviderRedirect.current;
        awaitingProviderRedirect.current = false;
        finishPreparation();
        if (shouldOpenProvider) openProvider(next.providerUrl);
      } else {
        keepPreparing = true;
        requestStatusRefresh.current();
      }
    } catch (error: any) {
      if (!mounted.current || controller.signal.aborted) return;
      const status = error?.response?.status;
      if (status === 410) {
        setFatalState("expired");
        clearVerificationToken(window.sessionStorage);
      } else if (error?.response?.data?.code === "provider_unavailable") {
        setStartError(
          "O serviço de verificação está temporariamente indisponível. Nenhuma aprovação ou recusa foi registrada; tente novamente mais tarde.",
        );
      } else if (isRecoverableStartTransition(error)) {
        keepPreparing = true;
        ambiguousStartOutcome.current =
          !error?.response || status === 408 || status === 425 || status >= 500;
        requestStatusRefresh.current();
      } else if (status === 429) {
        setStartError("Recebemos muitas tentativas. Aguarde um instante e tente novamente.");
      } else {
        setStartError(
          getUserFacingError(error, {
            context: "verification",
            fallback:
              "Não foi possível confirmar o início da verificação. Confira o status desta solicitação antes de tentar novamente.",
          }),
        );
      }
    } finally {
      if (startController.current === controller) {
        startController.current = null;
      }
      startInFlight.current = false;
      if (mounted.current && !keepPreparing) {
        awaitingProviderRedirect.current = false;
        ambiguousStartOutcome.current = false;
        finishPreparation();
      }
    }
  }

  if (loading) {
    return (
      <main className="representative-verification-page verification-page-state">
        <span className="verification-page-spinner" aria-label="Carregando" />
        <p>Consultando o andamento da verificação…</p>
      </main>
    );
  }

  if (fatalState) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faTriangleExclamation}
          title={fatalState === "expired" ? "Link expirado" : "Link indisponível"}
          body={
            fatalState === "expired"
              ? "Este convite expirou sem aprovar a conta. Esta tentativa não reserva o CNPJ; se a empresa ainda não tiver acesso, um representante legítimo pode enviar uma nova solicitação."
              : "Este link não existe ou não está mais disponível. Confira o e-mail recebido antes de tentar novamente."
          }
          tone="danger"
        >
          {fatalState === "expired" && (
            <a
              href="/solicitar-acesso"
              onClick={() => {
                clearVerificationToken(window.sessionStorage);
                clearAccessRequestSubmissionId(window.sessionStorage);
              }}
            >
              Enviar uma nova solicitação
            </a>
          )}
        </StatePanel>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faTriangleExclamation}
          title="Não foi possível consultar"
          body={loadError || "A verificação está temporariamente indisponível."}
          tone="warning"
        >
          <button type="button" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRotateRight} /> Tentar novamente
          </button>
        </StatePanel>
      </main>
    );
  }

  if (
    preparationTimedOut &&
    !session.providerUrl &&
    (session.verificationStatus === "starting" || session.verificationStatus === "provider_error")
  ) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faClock}
          title="Sua sessão ainda está sendo preparada"
          body="A preparação continua no servidor. Você não precisa iniciar outro cadastro nem pedir uma nova sessão; consulte o andamento da mesma solicitação."
          tone="warning"
        >
          <button type="button" onClick={() => requestStatusRefresh.current()}>
            <FontAwesomeIcon icon={faRotateRight} /> Consultar andamento
          </button>
        </StatePanel>
        {pollWarning && (
          <p className="verification-page-poll-warning" role="status">
            {pollWarning}
          </p>
        )}
      </main>
    );
  }

  if (
    starting ||
    ((session.verificationStatus === "starting" ||
      session.verificationStatus === "provider_error") &&
      !session.providerUrl)
  ) {
    return (
      <main className="representative-verification-page verification-page-state" aria-busy="true">
        <StatePanel
          icon={faClock}
          title="Preparando sua verificação"
          body={
            preparationSlow
              ? "O fornecedor está demorando mais que o normal, mas sua solicitação continua segura. Estamos consultando o andamento sem criar outra sessão automaticamente."
              : "Estamos criando ou recuperando sua sessão segura. Você não precisa clicar novamente; abriremos o fornecedor automaticamente quando estiver pronta."
          }
          tone="neutral"
        >
          <div className="verification-page-preparation" role="status">
            <span className="verification-page-spinner" aria-hidden="true" />
            <span>Consultando a sessão no servidor…</span>
          </div>
        </StatePanel>
        {pollWarning && (
          <p className="verification-page-poll-warning" role="status">
            {pollWarning}
          </p>
        )}
      </main>
    );
  }

  const terminalNonApproval =
    session.outcomeClass === "hard_declined" || session.outcomeClass === "retryable_not_approved";
  const rejected = terminalNonApproval && session.outcomeSettled;
  const settlingNonApproval = terminalNonApproval && !session.outcomeSettled;
  const manualReview =
    session.verificationStatus === "manual_review" || session.authorityStatus === "manual_review";
  const providerReview =
    session.outcomeClass === "pending" &&
    session.providerStatus.trim().toUpperCase() === "IN_REVIEW";
  const finalApproved = isFinalApprovedVerification(session);
  const readyToActivate =
    finalApproved &&
    !activationRecoveryActive &&
    !session.activationClaimed &&
    Boolean(session.activation);

  if (accessAlreadyReleased || (finalApproved && session.activation && session.activationClaimed)) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faCheck}
          title={accessAlreadyReleased ? "Acesso já liberado" : "Acesso enviado por e-mail"}
          body={
            accessAlreadyReleased
              ? "Use o link mais recente enviado ao seu e-mail para criar sua senha ou entre com a conta que já foi registrada."
              : "A verificação foi aprovada. Use o link mais recente enviado ao seu e-mail para criar sua senha e entrar na plataforma."
          }
          tone="success"
        />
      </main>
    );
  }

  if (rejected) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faTriangleExclamation}
          title="Verificação não aprovada"
          body="Não foi possível concluir esta solicitação. Por segurança, não detalhamos os critérios da decisão. Esta tentativa não cria uma nova reserva sobre o CNPJ; se a empresa ainda não tiver acesso, um representante legítimo pode enviar outro cadastro."
          tone="danger"
        >
          {automaticPollingPaused && (
            <button type="button" onClick={() => requestStatusRefresh.current()}>
              <FontAwesomeIcon icon={faRotateRight} /> Atualizar decisão
            </button>
          )}
          <a
            href="/solicitar-acesso"
            onClick={() => {
              clearVerificationToken(window.sessionStorage);
              clearAccessRequestSubmissionId(window.sessionStorage);
            }}
          >
            Enviar uma nova solicitação
          </a>
        </StatePanel>
      </main>
    );
  }

  if (readyToActivate || activating || (finalApproved && activationError)) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faCheck}
          title="Verificação aprovada"
          body={
            activating
              ? "Estamos preparando a etapa segura para você criar sua senha."
              : "Sua identidade e seus poderes de representação foram confirmados."
          }
          tone="success"
        >
          {activating ? (
            <span className="verification-page-spinner" aria-label="Preparando acesso" />
          ) : (
            <button type="button" onClick={() => void continueToPassword()}>
              Criar minha senha
            </button>
          )}
        </StatePanel>
        {activationError && (
          <p className="verification-page-submit-error" role="alert">
            {activationError}
          </p>
        )}
      </main>
    );
  }

  if (providerReview) {
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faClock}
          title="Verificação em análise"
          body="O fornecedor recebeu suas informações e iniciou uma análise adicional. Você não precisa reiniciar nem enviar outro cadastro. Acompanharemos a mesma sessão e avisaremos por e-mail assim que houver uma decisão."
          tone="neutral"
        >
          {automaticPollingPaused ? (
            <button type="button" onClick={() => requestStatusRefresh.current()}>
              <FontAwesomeIcon icon={faRotateRight} /> Atualizar status
            </button>
          ) : (
            <span className="verification-page-spinner" aria-label="Acompanhando análise" />
          )}
        </StatePanel>
        {pollWarning && (
          <p className="verification-page-poll-warning" role="status">
            {pollWarning}
          </p>
        )}
      </main>
    );
  }

  if (
    session.verificationStatus === "starting" ||
    session.verificationStatus === "in_progress" ||
    session.verificationStatus === "manual_review" ||
    session.verificationStatus === "provider_error" ||
    settlingNonApproval ||
    activationRecoveryActive ||
    (session.verificationStatus === "approved" && !finalApproved) ||
    manualReview
  ) {
    const waitingForAuthority =
      activationRecoveryActive || session.verificationStatus === "approved" || manualReview;
    const settlingDecision = settlingNonApproval;
    const confirmingProviderResult =
      returnedFromProvider && !waitingForAuthority && !providerReturnResumeAvailable;
    const providerLinkReady = Boolean(session.providerUrl);
    const recoveringProviderLink =
      !waitingForAuthority && !confirmingProviderResult && !providerLinkReady;
    return (
      <main className="representative-verification-page verification-page-state">
        <StatePanel
          icon={faClock}
          title={
            automaticPollingPaused
              ? "Consulte o andamento"
              : settlingDecision
                ? "Confirmando o resultado"
                : waitingForAuthority
                  ? "Finalizando sua verificação"
                  : confirmingProviderResult
                    ? "Confirmando o resultado"
                    : recoveringProviderLink
                      ? "Recuperando sua verificação"
                      : "Verificação em andamento"
          }
          body={
            automaticPollingPaused
              ? "O prazo de atualização automática terminou. Consulte o servidor para buscar a decisão mais recente; sua solicitação continua salva e nenhuma nova sessão será criada."
              : settlingDecision
                ? "O fornecedor concluiu a jornada e nosso servidor está estabilizando a decisão automática. Não envie outro cadastro; esta página mostrará apenas o resultado final."
                : waitingForAuthority
                  ? "Estamos conferindo automaticamente as confirmações finais antes de liberar sua conta. Você não precisa enviar nada nem aguardar uma análise manual."
                  : confirmingProviderResult
                    ? "Recebemos seu retorno do fornecedor e estamos confirmando o resultado no servidor. Você pode aguardar nesta página; também avisaremos por e-mail quando houver uma decisão."
                    : recoveringProviderLink
                      ? "A sessão já foi iniciada. Estamos recuperando o acesso seguro ao fornecedor; você não precisa começar outro cadastro."
                      : "Conclua as etapas no fornecedor. Esta página só avança depois que nosso servidor confirmar o resultado."
          }
          tone="neutral"
        >
          {automaticPollingPaused && (
            <button type="button" onClick={() => requestStatusRefresh.current()}>
              <FontAwesomeIcon icon={faRotateRight} /> Atualizar status
            </button>
          )}
          {!automaticPollingPaused &&
            (settlingDecision ||
              waitingForAuthority ||
              confirmingProviderResult ||
              recoveringProviderLink) && (
              <span className="verification-page-spinner" aria-label="Atualizando verificação" />
            )}
          {!automaticPollingPaused &&
            !waitingForAuthority &&
            !confirmingProviderResult &&
            providerLinkReady && (
              <button type="button" onClick={() => openProvider(session.providerUrl!)}>
                Continuar verificação <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </button>
            )}
        </StatePanel>
        {pollWarning && (
          <p className="verification-page-poll-warning" role="status">
            {pollWarning}
          </p>
        )}
        {(providerUrlError || startError) && (
          <p className="verification-page-submit-error" role="alert">
            {providerUrlError || startError}
          </p>
        )}
      </main>
    );
  }

  return (
    <main className="representative-verification-page">
      <RepresentativeVerificationHeader />

      <section className="verification-page-form">
        {(startError || loadError) && (
          <div className="verification-page-submit-error" role="alert">
            {startError || loadError}
          </div>
        )}

        <div className="verification-page-section">
          <div className="verification-page-section-heading">
            <span aria-hidden="true">
              <FontAwesomeIcon icon={faShieldHalved} />
            </span>
            <div>
              <h2>Antes de começar</h2>
              <p>
                Tenha um documento oficial em mãos. O fornecedor pode solicitar prova de vida e
                evidências de representação, conforme o seu caso.
              </p>
            </div>
          </div>
          <p className="verification-page-data-notice">{session.consentPolicy.notice}</p>
          {session.consentPolicy.providerRetentionDays > 0 && (
            <p className="verification-page-data-notice">
              Prazo máximo configurado no fornecedor para esta jornada:{" "}
              <strong>{session.consentPolicy.providerRetentionDays} dias</strong>.
            </p>
          )}
          <p className="verification-page-legal-links">
            Consulte a{" "}
            <a
              href={session.consentPolicy.daKasaPrivacyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Privacidade da DaKasa
            </a>
            , o{" "}
            <a
              href={session.consentPolicy.providerPrivacyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Aviso de Privacidade da {session.consentPolicy.provider}
            </a>{" "}
            e os{" "}
            <a
              href={session.consentPolicy.providerTermsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Termos de Verificação da {session.consentPolicy.provider}
            </a>
            .
          </p>
          <label className={`verification-page-consent${consentError ? " has-error" : ""}`}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => {
                setConsent(event.target.checked);
                if (event.target.checked) setConsentError(null);
              }}
            />
            <span>Li os avisos acima e confirmo a autorização descrita para esta verificação.</span>
          </label>
          {consentError && <span className="verification-page-error">{consentError}</span>}
        </div>

        <button
          type="button"
          className="verification-page-submit"
          onClick={() => void startVerification()}
          disabled={starting}
        >
          {starting ? "Iniciando com segurança…" : "Iniciar verificação segura"}
        </button>
        <small className="verification-page-privacy">
          A sessão paga só é criada depois deste clique. O retorno do fornecedor nunca é tratado
          como aprovação até que o servidor DaKasa confirme o resultado.
        </small>
      </section>
    </main>
  );
};

export default RepresentativeVerificationPage;
